/**
 * Login-wall bypass resolution (auth hardening, #116/#117 fork lockdown).
 *
 * HARD RULE: the login wall can NEVER be bypassed on a PRODUCT build. A client
 * clone (Codan / any ITAR fork) must always require authentication, so
 * `VITE_SKIP_AUTH` is ignored there — even if someone sets it, it does nothing.
 * The bypass exists ONLY for the internal build, as a developer convenience:
 *   • an explicit, auditable `VITE_SKIP_AUTH=true`, or
 *   • the local Vite dev server running on localhost (dead in any deployed build).
 *
 * Pure + injectable so the security invariant ("product ⇒ auth always on") is
 * locked by a unit test rather than living implicitly in App.tsx.
 */
import { isInternalBuild } from './productSurface'

export interface SkipAuthInputs {
  /** VITE_SKIP_AUTH === 'true' */
  viteSkipAuth: boolean
  /** Host is localhost / 127.0.0.1 */
  isLocalhost: boolean
  /** import.meta.env.DEV (true only under the Vite dev server, never in a build) */
  isDev: boolean
  /** Defaults to isInternalBuild(); injectable for tests. */
  internalBuild?: boolean
}

export function resolveSkipAuth(opts: SkipAuthInputs): boolean {
  const internal = opts.internalBuild ?? isInternalBuild()
  if (!internal) return false // PRODUCT build: the login wall is mandatory, no exceptions.
  return opts.viteSkipAuth || (opts.isLocalhost && opts.isDev)
}
